import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { FrontendLoggerService } from '../services/frontend-logger.service';
import { NotificationService } from '../services/notification.service';
import { TokenService } from '../services/token.service';
import { Router } from '@angular/router';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { isProviderOAuthBackendCallbackEndpoint } from '../services/auth/provider-oauth-callback.util';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private _logger?: FrontendLoggerService;
  private _notifier?: NotificationService;
  private _tokenService?: TokenService;
  private _router?: Router;
  private lastNotifyTs = 0;
  private redirectingToLogin = false;

  // Endpoints públicos que no requieren autenticación
  private readonly publicEndpoints: string[] = [
    '/invites/validate-token',
    '/onboarding/invite',
    '/onboarding/accept',
    '/auth/login',
    '/auth/signup',
    '/rag/status'
  ];

  constructor(
    private readonly authService: AuthService,
    private readonly injector: Injector
  ) {}

  private get logger(): FrontendLoggerService {
    // Lazy resolve to avoid circular DI at construction time
    this._logger ??= this.injector.get(FrontendLoggerService);
    return this._logger;
  }

  private get notifier(): NotificationService {
    this._notifier ??= this.injector.get(NotificationService);
    return this._notifier;
  }

  private get tokenService(): TokenService {
    this._tokenService ??= this.injector.get(TokenService);
    return this._tokenService;
  }

  private get router(): Router | undefined {
    try { return this._router || (this._router = this.injector.get(Router)); } catch { return undefined; }
  }

  private currentView(): string {
    const routeUrl = (this.router?.url || '').trim();
    if (routeUrl && routeUrl !== '/') {
      return routeUrl;
    }

    const locPath = globalThis.location?.pathname ?? '';
    const locSearch = globalThis.location?.search ?? '';
    const resolved = `${locPath}${locSearch}`;

    return resolved || routeUrl || 'desconocida';
  }

  private safeStringify(value: unknown): string {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  private notifyOnce(message: string) {
    const now = Date.now();
    if (now - this.lastNotifyTs < 30000) return; // throttle 30s
    this.lastNotifyTs = now;
    try {
      this.notifier.addNotification({ type: 'warning', message });
    } catch (e) {
      this.logger.warn('[AuthInterceptor] Failed to push notification: ' + String(e));
    }
  }

  private redirectToLogin(returnUrl?: string): void {
    if (this.redirectingToLogin) return;
    this.redirectingToLogin = true;

    const path = this.router?.url || globalThis.location?.pathname || '/';
    const current = returnUrl || path;
    const isAuthScreen = current.startsWith('/login') || current.startsWith('/signup');
    if (isAuthScreen) {
      this.redirectingToLogin = false;
      return;
    }

    this.router?.navigate(['/login'], { queryParams: { returnUrl: current } })
      .finally(() => {
        this.redirectingToLogin = false;
      });
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isProviderOAuthCallback = isProviderOAuthBackendCallbackEndpoint(request.url);

    // Solo aplicar a llamadas hacia nuestra API
    if (!request.url.includes(environment.apiUrl)) {
      return next.handle(request);
    }

    // Evitar loops con el endpoint de logs
    if (request.url.includes('/rag/frontend-log')) {
      return next.handle(request);
    }

    // Permitir endpoints públicos sin autenticación
    if (this.isPublicEndpoint(request.url)) {
      return next.handle(request);
    }

    // 1) Esperar a que el SDK termine de cargar
    const loading$ = (this.authService.isLoading$ as Observable<boolean>);
    const authed$  = this.authService.isAuthenticated$;

    return loading$.pipe(
      filter((loading) => loading === false),
      take(1),
      switchMap(() => authed$.pipe(take(1))),
      switchMap((isAuthed) => {
        if (!isAuthed) {
          if (isProviderOAuthCallback) {
            return next.handle(request);
          }

          // Bloquear la llamada devolviendo 401 para cualquier endpoint de API si no hay autenticación
          const err = new HttpErrorResponse({
            status: 401,
            statusText: 'Unauthorized',
            url: request.url,
            error: { message: 'Request blocked: user not authenticated' }
          });
          this.logger.warn('Bloqueando request no autenticada', request.url);
          this.notifyOnce(`No se pudo autenticar correctamente. Inicia sesión nuevamente. Vista: ${this.currentView()}`);
          this.redirectToLogin(this.currentView());
          return throwError(() => err);
        }

        // 2) Obtener token y adjuntarlo si no existe
        return this.tokenService.getToken().pipe(
          switchMap((token) => {
            if (!token) {
              if (isProviderOAuthCallback) {
                return next.handle(request);
              }

              // Sin token: bloquear como 401
              const err = new HttpErrorResponse({
                status: 401,
                statusText: 'Unauthorized (empty token)',
                url: request.url,
                error: { message: 'Empty token after authentication' }
              });
              this.logger.warn('Token vacío tras autenticación, bloqueando', request.url);
              this.notifyOnce(`No se pudo obtener el token de acceso. Inicia sesión nuevamente. Vista: ${this.currentView()}`);
              this.redirectToLogin(this.currentView());
              return throwError(() => err);
            }

            const hasAuthHeader = !!request.headers.get('Authorization');
            const reqToSend = hasAuthHeader
              ? request
              : request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
            return next.handle(reqToSend);
          })
        );
      }),
      catchError((error) => {
        const isExpectedNextAction404 =
          error?.status === 404 && request.url.includes('/api/home-ia/next-action');
        if (globalThis.location?.pathname !== '/login' && !isExpectedNextAction404) {
          const status = (error && (error.status || error.statusCode)) ?? 'n/a';
          const label = status === 401 ? 'Auth/Gating' : 'HTTP';
          const details = {
            url: request.url,
            status,
            statusText: error?.statusText,
            errorMessage: error?.message || error,
            errorBody: error?.error
          };
          this.logger.error(`[${label}] Error en interceptor`, this.safeStringify(details));
        }

        if (error?.status === 401 && !isProviderOAuthCallback) {
          this.notifyOnce(`Tu sesión expiró. Inicia sesión nuevamente. Vista: ${this.currentView()}`);
          this.redirectToLogin(this.currentView());
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Verifica si la URL corresponde a un endpoint público
   * que no requiere autenticación
   */
  private isPublicEndpoint(url: string): boolean {
    return this.publicEndpoints.some(endpoint => url.includes(endpoint));
  }
}
